import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Ad slot shown ONLY to free-tier users (or users whose subscription is not active).
 * Disappears the moment a user upgrades to Pro or Business.
 *
 * Variants:
 *   - "banner"  → full-width horizontal, ideal for tops of pages
 *   - "card"    → compact block, ideal inline in grids/sidebars
 */
export default function AdSlot({ variant = "banner", className = "" }) {
  const { user } = useAuth();
  const isPaid = user && (user.subscription_status === "active") && (user.plan === "pro" || user.plan === "business");
  if (isPaid) return null;

  if (variant === "card") {
    return (
      <div
        data-testid="ad-slot-card"
        className={`relative p-5 rounded-md border border-dashed border-[#F59E0B]/40 bg-gradient-to-br from-[#FFFBEB] to-white ${className}`}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F59E0B] bg-[#F59E0B]/10 px-1.5 py-0.5 rounded">Ad</span>
          <Link to="/plans" className="text-[11px] font-semibold text-[#1F3A8A] hover:underline flex items-center gap-1" data-testid="ad-remove-link">
            <X className="w-3 h-3" /> Remove ads
          </Link>
        </div>
        <div className="font-display font-bold text-[#111827] leading-snug">Your ad could reach thousands of prompt engineers.</div>
        <a href="mailto:hello@promptyai.app?subject=Advertise%20on%20Promptyai" className="text-xs text-[#64748B] mt-1 block hover:text-[#1F3A8A]">hello@promptyai.app →</a>
      </div>
    );
  }

  return (
    <div
      data-testid="ad-slot-banner"
      className={`relative rounded-md border border-dashed border-[#F59E0B]/40 bg-gradient-to-r from-[#FFFBEB] via-white to-[#FFFBEB] px-4 sm:px-6 py-3 sm:py-4 ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#F59E0B] bg-[#F59E0B]/10 px-1.5 py-0.5 rounded self-start sm:self-auto">Ad</span>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-[#111827] text-sm sm:text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#F59E0B] shrink-0" />
            <span className="truncate">Reach prompt engineers who ship. Advertise on Promptyai.app.</span>
          </div>
          <a href="mailto:hello@promptyai.app?subject=Advertise%20on%20Promptyai" className="text-xs text-[#64748B] hover:text-[#1F3A8A]">hello@promptyai.app</a>
        </div>
        <Link to="/plans" className="shrink-0" data-testid="ad-upgrade-link">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1F3A8A] hover:bg-[#1E40AF] text-white text-xs font-semibold transition-colors">
            <X className="w-3 h-3" /> Remove ads — Upgrade
          </span>
        </Link>
      </div>
    </div>
  );
}
